import React, { useEffect, useState } from "react";
import { Plus, Save, Trash2, Pencil, X, Upload } from "lucide-react";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const HeroSectionForm = ({
  slides = [],
  onSave,
  onRefetch,
  apiBase = `${API_BASE_URL}/about`,
}) => {
  const [rows, setRows] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newSlide, setNewSlide] = useState({
    heading: "",
    subheading: "",
    tagline: "",
    location: "",
    images: [],
  });

  useEffect(() => {
    if (!Array.isArray(slides)) return;
    const normalized = slides.map((s) => {
      let images = [];
      try {
        if (Array.isArray(s.images)) {
          images = s.images;
        } else if (typeof s.images === "string" && s.images) {
          const parsed = JSON.parse(s.images);
          images = Array.isArray(parsed) ? parsed : [];
        }
      } catch {
        images = [];
      }
      return { ...s, images };
    });
    const hasChanged = JSON.stringify(normalized) !== JSON.stringify(rows);
    if (hasChanged) {
      setRows(normalized);
    }
  }, [slides]);

  const resetForm = () => {
    setNewSlide({
      heading: "",
      subheading: "",
      tagline: "",
      location: "",
      images: [],
    });
    setEditingId(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewSlide((prev) => ({ ...prev, [name]: value }));
  };

  // Enhanced handleImageUpload with file type + aspect ratio validation
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!validTypes.includes(file.type.toLowerCase())) {
      alert("Invalid file type. Please upload JPG or PNG images only.");
      e.target.value = "";
      return;
    }

    // Validate image dimensions (16:9)
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      const width = img.width;
      const height = img.height;
      const aspectRatio = width / height;
      const targetRatio = 16 / 9;
      const tolerance = 0.01;

      if (Math.abs(aspectRatio - targetRatio) > tolerance) {
        alert(
          `Invalid aspect ratio. Please upload an image with 16:9 aspect ratio.\nYour image: ${width}×${height} (${aspectRatio.toFixed(
            2
          )}:1)\nRequired: 16:9 (1.78:1)`
        );
        URL.revokeObjectURL(objectUrl);
        e.target.value = "";
        return;
      }

      // Upload if validation passes
      const formData = new FormData();
      formData.append("media", file);

      axios
        .post(`${API_BASE_URL}/admin/upload-media`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then((res) => {
          console.log("upload response", res.data);
          const uploadedUrl = res.data.data.files?.[0]?.url;
          if (uploadedUrl) {
            setNewSlide((prev) => ({
              ...prev,
              images: [uploadedUrl],
            }));
          }
        })
        .catch((err) => {
          console.error("upload-media error", err);
          alert("Failed to upload image. Please try again.");
        })
        .finally(() => {
          URL.revokeObjectURL(objectUrl);
          e.target.value = "";
        });
    };

    img.onerror = () => {
      alert("Failed to load image. Please try another file.");
      URL.revokeObjectURL(objectUrl);
      e.target.value = "";
    };

    img.src = objectUrl;
  };

  const handleRemoveImage = () => {
    setNewSlide((prev) => ({ ...prev, images: [] }));
  };

  const handleEdit = (slide) => {
    setEditingId(slide.hero_id || null);
    setNewSlide({
      heading: slide.heading || "",
      subheading: slide.subheading || "",
      tagline: slide.tagline || "",
      location: slide.location || "",
      images: Array.isArray(slide.images) ? slide.images : [],
    });
    setFormOpen(true);
  };

  const handleDelete = async (id) => {
    if (
      !id ||
      !window.confirm("Are you sure you want to delete this hero slide?")
    )
      return;

    const token = localStorage.getItem("token");
    if (!token) {
      alert("Session expired. Please log in again.");
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/home/hero/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error("Server returned error.");

      setRows((prev) => prev.filter((r) => r.hero_id !== id));
      onRefetch?.();
    } catch (err) {
      console.error("Delete failed:", err);
      alert("Failed to delete slide. Please check console for details.");
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();

    const payload = {
      heading: newSlide.heading.trim(),
      subheading: newSlide.subheading.trim(),
      tagline: newSlide.tagline.trim(),
      location: newSlide.location.trim(),
      images: Array.isArray(newSlide.images) ? newSlide.images : [],
    };

    if (
      !payload.heading ||
      !payload.subheading ||
      !payload.tagline ||
      !payload.location
    ) {
      alert("All fields are required.");
      return;
    }

    try {
      const mode = editingId ? "update" : "create";
      await onSave({ ...payload, hero_id: editingId, mode });
      resetForm();
      setFormOpen(false);
      onRefetch?.();
    } catch (err) {
      console.error("Error saving slide:", err);
      alert("Failed to save slide. Please check console for details.");
    }
  };

  return (
    <section className="bg-white rounded-xl shadow-md sm:shadow-lg p-4 sm:p-6 md:p-8 mb-10 w-full">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
          <div>
            <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-blue-700">
              Hero Slides
            </h2>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">
              Manage hero banners for your homepage in one place.
            </p>
          </div>
          <button
            onClick={() => {
              if (formOpen) resetForm();
              setFormOpen((p) => !p);
            }}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm md:text-base"
          >
            <Plus size={16} className="sm:w-[18px]" />
            {formOpen ? "Close" : "Add Slide"}
          </button>
        </div>

        {/* Table */}
        <div className="hidden sm:block">
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="min-w-full text-xs sm:text-sm">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Title</th>
                  <th className="px-4 py-3 text-left font-semibold">
                    Location
                  </th>
                  <th className="px-4 py-3 text-left font-semibold">
                    Preview
                  </th>
                  <th className="px-4 py-3 text-center font-semibold">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.length > 0 ? (
                  rows.map((slide) => (
                    <tr
                      key={slide.hero_id}
                      className="border-t hover:bg-gray-50 transition text-gray-700"
                    >
                      <td className="px-4 py-3 max-w-[220px] truncate">
                        {slide.heading}
                      </td>
                      <td className="px-4 py-3 max-w-[160px] truncate">
                        {slide.location}
                      </td>
                      <td className="px-4 py-3">
                        {slide.images?.length > 0 ? (
                          <img
                            src={slide.images[0]}
                            alt="preview"
                            className="h-10 w-16 sm:h-12 sm:w-20 object-cover rounded"
                          />
                        ) : (
                          <span className="text-gray-400 italic">
                            No image
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-center gap-3">
                          <button
                            type="button"
                            onClick={() => handleEdit(slide)}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(slide.hero_id)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={4}
                      className="text-center py-5 text-gray-500 italic text-sm"
                    >
                      No slides yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Form */}
        {formOpen && (
          <form
            onSubmit={handleSubmit}
            className="border-t pt-6 mt-4 space-y-6 text-sm sm:text-base"
          >
            <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-800">
              {editingId ? "Edit Slide" : "Add New Slide"}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              {/* Text Fields */}
              {["heading", "subheading", "tagline", "location"].map((field) => (
                <div key={field} className="space-y-1.5">
                  <label className="block text-sm font-medium text-gray-700 capitalize">
                    {field}
                  </label>
                  <input
                    name={field}
                    value={newSlide[field]}
                    onChange={handleChange}
                    required
                    className="w-full border rounded-lg px-3 py-2 sm:py-2.5 shadow-sm focus:ring-2 focus:ring-blue-400 outline-none text-sm sm:text-base"
                  />
                </div>
              ))}

              {/* Image Upload */}
              <div className="col-span-1 md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hero Image
                </label>
                {newSlide.images?.length > 0 ? (
                  <div className="relative w-full max-w-xs sm:max-w-md">
                    <img
                      src={newSlide.images[0]}
                      alt="Hero preview"
                      className="w-full h-44 sm:h-52 md:h-60 object-cover rounded-lg border"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-2 rounded-lg"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 sm:p-8 text-center">
                    <input
                      type="file"
                      id="home-main-image"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                    <label
                      htmlFor="home-main-image"
                      className="cursor-pointer flex flex-col items-center gap-2 sm:gap-3"
                    >
                      <Upload size={40} className="text-gray-400" />
                      <span className="text-gray-700 text-sm sm:text-base">
                        Click to upload an image
                      </span>
                      <span className="text-xs text-gray-400">
                        Recommended image size: <strong>1920×1080 px (16:9)</strong>, JPG/PNG, upto 500 KB
                      </span>
                    </label>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-4 flex justify-end">
              <button
                type="submit"
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 sm:px-6 py-2 sm:py-2.5 rounded-lg text-sm sm:text-base"
              >
                <Save size={18} />
                {editingId ? "Update Slide" : "Save Slide"}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
};

export default HeroSectionForm;
